import { useCallback, useRef } from 'react';
import ChatService, { StreamEvent } from '../services/ChatService';
import { Message } from '../components/features/chat/types';
import { isPersistedPartEditInstruction } from '../utils/partEdits';
import { markMessagesAsError, removeBotMessage } from '../utils/messageLifecycle';
import { finalizeMessage, mergeSuggestions, reduceFrame } from '../utils/streamReducer';
import { newMessageId } from '../utils/messageId';
import {
  detectOrderCreated,
  extractBackendTags,
  isOrderCreatedOnly,
  parseStreamEvent,
} from '../utils/streamEffects';
import { ContinuationFrame } from '../types/streamProtocol';
import i18n from 'i18next';

export interface StreamSendOptions {
  /** The text message to send. */
  message: string;
  /** The session ID to stream into. */
  sessionId: string;
  /** The current UI language. */
  language: string;
  /** Context tags for the request. */
  contextTags?: string[];
  /** Files to attach. */
  filesData?: Array<{ data: string; mimeType: string; gcsUri?: string }>;
  /**
   * Optional user message to prepend to the messages list.
   * Pass `null` to suppress (e.g. for hidden NEXT triggers).
   */
  userMessage: Message | null;
  /** Called when a stream event arrives. */
  onBackendTags?: (tags: string[]) => void;
  /**
   * Optional structured clarification answer threaded into the request body as
   * `structured_answer` (alongside the human-readable `message`).
   */
  structuredAnswer?: Record<string, unknown>;
}

export interface StreamSendResult {
  /** Kick off a streaming send. Returns the bot-message ID that was injected. */
  send: (opts: StreamSendOptions) => Promise<void>;
  /** Abort the current in-flight stream (if any). */
  abort: () => void;
  /** Whether a stream is currently in progress (ref, safe to read in async closures). */
  isStreamingRef: React.MutableRefObject<boolean>;
}

interface StreamSendDeps {
  /** Setter for the messages state. */
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  /** Setter for the loading flag. */
  setIsLoading: (v: boolean) => void;
  /** Setter for the retry-state banner. */
  setRetryState: (v: { isRetrying: boolean; attempt: number; maxAttempts: number; reason: string } | null) => void;
  /** Ref that tracks the current abort controller (shared with the session context). */
  activeAbortControllerRef: React.MutableRefObject<AbortController | null>;
  /** Setter for the React-state abort controller (for the stop-streaming button). */
  setAbortController: React.Dispatch<React.SetStateAction<AbortController | null>>;
  /** Returns true when targetSessionId is still the active session. */
  isSessionActive: (targetSessionId: string) => boolean;
  /** Called on each retry attempt so the UI can show a banner. */
  onRetry?: (attempt: number, maxAttempts: number, reason: string) => void;
  /**
   * Called when the stream ends with an error (after all retries are exhausted).
   * Use this to schedule a background history reload — the server may have
   * actually completed the request even though the client-side stream failed.
   */
  onStreamError?: (sessionId: string) => void;
  /**
   * Called when an ORDER_CREATED action is detected in a continuation event.
   * Receives the numeric order ID extracted from the action tag.
   * Use this to update the URL / refresh the associated order.
   */
  onOrderCreated?: (orderId: number) => void;
  /** Firebase analytics event logger. */
  logAnalyticsEvent?: (name: string, params: Record<string, any>) => void;
  /**
   * Admin-only part-selection mode ("best_effort" | "human_in_the_loop") to
   * attach to the outgoing request. Pass undefined for non-admins so the field
   * is omitted entirely and regular-user requests stay unchanged.
   */
  partSelectionMode?: string;
}

/**
 * Encapsulates the full streaming lifecycle for all send paths in ChatPage:
 *  - sets isStreamingRef BEFORE any async work
 *  - injects optimistic user + bot messages in one batched setState
 *  - clears isStreamingRef in ALL exit paths (onComplete / onError / catch)
 *  - uses the locally-created AbortController, not stale state
 *
 * Usage:
 *   const { send, abort, isStreamingRef } = useStreamSend(deps);
 */
export function useStreamSend(deps: StreamSendDeps): StreamSendResult {
  const {
    setMessages,
    setIsLoading,
    setRetryState,
    activeAbortControllerRef,
    setAbortController,
    isSessionActive,
    onRetry,
    onStreamError,
    onOrderCreated,
    logAnalyticsEvent,
    partSelectionMode,
  } = deps;

  // Single shared isStreamingRef owned by this hook instance.
  const isStreamingRef = useRef<boolean>(false);

  const abort = useCallback(() => {
    const ctrl = activeAbortControllerRef.current;
    if (ctrl) {
      ctrl.abort();
      activeAbortControllerRef.current = null;
      setAbortController(null);
      setIsLoading(false);
      // Caller is responsible for cleaning up messages (e.g. via stopStreaming).
    }
  }, [activeAbortControllerRef, setAbortController, setIsLoading]);

  const send = useCallback(async (opts: StreamSendOptions): Promise<void> => {
    const {
      message,
      sessionId,
      language,
      contextTags = [],
      filesData,
      userMessage,
      onBackendTags,
      structuredAnswer,
    } = opts;

    // ── 1. Build bot placeholder ──────────────────────────────────────────
    const botMessageId = newMessageId('bot');
    const botPlaceholder: Message = {
      id: botMessageId,
      text: '',
      vin: null,
      datasetId: null,
      sender: 'bot',
      timestamp: new Date(),
      isStreaming: true,
      events: [],
    };

    // ── 2. Create AbortController BEFORE setting any flags ────────────────
    const controller = new AbortController();
    setAbortController(controller);
    activeAbortControllerRef.current = controller;

    // ── 3. Set streaming flag SYNCHRONOUSLY ───────────────────────────────
    //    Must happen before any await so that URL-params effects that call
    //    fetchSessionDetails (and check this ref) see it immediately.
    isStreamingRef.current = true;
    setIsLoading(true);

    // Hidden part-edit commands still get a small optimistic change indicator.
    // It uses the command as data, while MessageList prevents that text from
    // ever appearing as a normal user bubble.
    const visibleUserMessage = userMessage ?? (isPersistedPartEditInstruction(message) ? {
      id: newMessageId('part_edit'),
      text: message,
      vin: null,
      datasetId: null,
      sender: 'user' as const,
      timestamp: new Date(),
      isStreaming: false,
    } : null);

    // ── 4. Inject optimistic messages in ONE batched update ───────────────
    if (visibleUserMessage) {
      setMessages(prev => [...prev, visibleUserMessage, botPlaceholder]);
    } else {
      setMessages(prev => [...prev, botPlaceholder]);
    }

    const userMessageId = visibleUserMessage?.id ?? '';

    // ── 5. Helpers ────────────────────────────────────────────────────────
    const clearStreaming = () => {
      isStreamingRef.current = false;
    };

    // Tracks which bot message ID is currently receiving stream events.
    // When a has_continuation final arrives we create a new placeholder and
    // point this ref at it so subsequent events land in the right bubble.
    const activeBotMessageIdRef = { current: botMessageId };

    // When an earlier frame in this stream has already surfaced parts_results,
    // the backend echoes those same parts in the follow-up bubble's final event
    // as session context.  We must not surface them again or we get a duplicate
    // MultiPartResultView.  Only set when a frame actually carried parts —
    // a parts-free continuation (e.g. VIN confirmation) must NOT cause the
    // terminal final's genuinely new parts_result to be stripped.
    const partsAlreadySurfacedRef = { current: false };
    const markPartsSurfaced = (frame: { payload: { parts_result?: any[] } }) => {
      if ((frame.payload.parts_result?.length ?? 0) > 0) {
        partsAlreadySurfacedRef.current = true;
      }
    };

    // Reset the active bot bubble's text and events before a retry so that
    // already-streamed content doesn't get re-displayed when the backend
    // re-emits events from the SSE resume offset.
    const resetActiveBubble = () => {
      const id = activeBotMessageIdRef.current;
      setMessages(prev =>
        prev.map(msg =>
          msg.id !== id
            ? msg
            : { ...msg, text: '', events: [], isStreaming: true },
        ),
      );
    };

    // Wrapped onRetry that resets the bubble before surfacing the retry banner.
    const onRetryWithReset = onRetry
      ? (attempt: number, maxAttempts: number, reason: string) => {
          resetActiveBubble();
          onRetry(attempt, maxAttempts, reason);
        }
      : () => { resetActiveBubble(); };

    // ── 6. Stream ─────────────────────────────────────────────────────────
    // Debug clock: send → terminal `final` frame, i.e. until the human is
    // needed again (shown under the bubble in admin debug mode).
    const sentAt = performance.now();
    try {
      await ChatService.sendMessageStream(
        message,
        sessionId,
        language,

        // onEvent — dispatches on the discriminated `StreamFrame` union.
        // All per-frame logic lives in the pure helpers from
        // `streamReducer` / `streamEffects`; this callback only wires
        // them up to React state + side-effect callbacks.
        (event: StreamEvent) => {
          const frame = parseStreamEvent(event);
          const currentId = activeBotMessageIdRef.current;

          if (event.type === 'final') {
            const clientMs = Math.round(performance.now() - sentAt);
            const server = (event.data as any)?.timing ?? null;
            setMessages(prev =>
              prev.map(msg => (msg.id !== currentId ? msg : { ...msg, turnTiming: { clientMs, server } })),
            );
          }

          switch (frame.kind) {
            case 'error':
              // Transport-level errors are surfaced via the onError callback
              // below — nothing to do for the per-event channel.
              return;

            case 'quota': {
              // Terminal plan-quota frame: finalize the pending bubble as an
              // in-conversation notice (ChatService already published to the
              // quota bus, which disables the composer via QuotaContext).
              const resetDate = frame.resetsAt
                ? new Date(frame.resetsAt).toLocaleDateString(i18n.language, {
                    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
                  })
                : '';
              // (i18n as any): the project's TS 4.9 cannot parse i18next's
              // typings for direct i18n.t calls — components use
              // useTranslation() and are unaffected.
              const notice = (i18n as any).t('usage.pausedNotice', {
                defaultValue:
                  "You've reached your plan's monthly LLM query limit. New queries are paused until {{date}} — upgrade your plan to restore access immediately.",
                date: resetDate,
              }) as string;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id !== currentId ? msg : finalizeMessage({ ...msg, text: notice }),
                ),
              );
              return;
            }

            case 'intermediate':
              setMessages(prev =>
                prev.map(msg => (msg.id !== currentId ? msg : reduceFrame(msg, frame))),
              );
              return;

            case 'suggestions':
              // The cards are already on screen (in whichever bubble holds
              // them); attach their suggestions by uuid.
              setMessages(prev => mergeSuggestions(prev, frame.payload.parts_result || []));
              return;

            case 'final': {
              // ── ORDER_CREATED side effect on a terminal final ───────────
              // The backend can emit two consecutive `is_final=true,
              // has_continuation=false` events on the same bubble — first the
              // silent ORDER_CREATED envelope, then the real greeting/text.
              // Without this branch the ORDER_CREATED HTML would overwrite
              // `message.text`, get latched into the typing-animation
              // snapshot, and trigger `isOrderOnlyMessage` in BotMessage,
              // hiding the bubble entirely (the user never sees the greeting
              // that arrives a few ms later).
              //
              // Treat ORDER_CREATED finals the same way as ORDER_CREATED
              // continuations: fire the callback, drop the silent bubble,
              // open a fresh placeholder so the next final lands cleanly.
              const orderCreated = detectOrderCreated(
                { ...frame, kind: 'continuation' } as ContinuationFrame,
              );
              if (orderCreated.isOrderCreated) {
                if (orderCreated.orderId != null && onOrderCreated) {
                  onOrderCreated(orderCreated.orderId);
                }
                const newBotId = newMessageId('bot');
                const newBotPlaceholder: Message = {
                  id: newBotId,
                  text: '',
                  vin: null,
                  datasetId: null,
                  sender: 'bot',
                  timestamp: new Date(),
                  isStreaming: true,
                  events: [],
                };
                setMessages(prev => [
                  ...prev.filter(msg => msg.id !== currentId),
                  newBotPlaceholder,
                ]);
                markPartsSurfaced(frame);
                activeBotMessageIdRef.current = newBotId;
                return;
              }

              // Terminal final (no continuation). Merge as a final event so
              // `finalizeMessage` (run in onComplete) can pick it up, then
              // update backend tags eagerly while streaming so the
              // ContextTagsBar refreshes immediately.
              // If an earlier frame already surfaced parts_results in this
              // stream, strip parts_result from this frame — the backend echoes
              // the previous turn's results as session context, but rendering
              // them again would produce a duplicate MultiPartResultView.
              const finalFrameToCont = partsAlreadySurfacedRef.current
                ? ({ ...frame, kind: 'continuation', payload: { ...frame.payload, parts_result: [] } } as ContinuationFrame)
                : ({ ...frame, kind: 'continuation' } as ContinuationFrame);
              markPartsSurfaced(frame);
              setMessages(prev =>
                prev.map(msg =>
                  msg.id !== currentId
                    ? msg
                    : reduceFrame(msg, finalFrameToCont),
                ),
              );
              if (onBackendTags && isSessionActive(sessionId)) {
                const tags = extractBackendTags(frame);
                if (tags) onBackendTags(tags);
              }
              return;
            }

            case 'continuation': {
              // ── Continuation error: finalise current bubble as error,
              //    do NOT open a new bubble.
              if (frame.hasError) {
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id !== currentId ? msg : finalizeMessage(reduceFrame(msg, frame)),
                  ),
                );
                if (onBackendTags && isSessionActive(sessionId)) {
                  const tags = extractBackendTags(frame);
                  if (tags) onBackendTags(tags);
                }
                return;
              }

              // ── ORDER_CREATED side effect: silently drop current bubble,
              //    fire callback, open fresh placeholder for the next turn.
              const orderCreated = detectOrderCreated(frame);
              if (orderCreated.isOrderCreated) {
                if (orderCreated.orderId != null && onOrderCreated) {
                  onOrderCreated(orderCreated.orderId);
                }
                const newBotId = newMessageId('bot');
                const newBotPlaceholder: Message = {
                  id: newBotId,
                  text: '',
                  vin: null,
                  datasetId: null,
                  sender: 'bot',
                  timestamp: new Date(),
                  isStreaming: true,
                  events: [],
                };
                setMessages(prev => [
                  ...prev.filter(msg => msg.id !== currentId),
                  newBotPlaceholder,
                ]);
                markPartsSurfaced(frame);
                activeBotMessageIdRef.current = newBotId;
                return;
              }

              // ── Normal continuation: freeze current bubble, open new one.
              markPartsSurfaced(frame);
              const newBotId = newMessageId('bot');
              const newBotPlaceholder: Message = {
                id: newBotId,
                text: '',
                vin: null,
                datasetId: null,
                sender: 'bot',
                timestamp: new Date(),
                isStreaming: true,
                events: [],
              };
              setMessages(prev => {
                const updated = prev.map(msg =>
                  msg.id !== currentId ? msg : reduceFrame(msg, frame),
                );
                return [...updated, newBotPlaceholder];
              });
              activeBotMessageIdRef.current = newBotId;
              return;
            }
          }
        },

        // onComplete
        () => {
          clearStreaming();
          const finalId = activeBotMessageIdRef.current;
          setMessages(prev => {
            const targetMsg = prev.find(msg => msg.id === finalId);
            // Empty continuation placeholder — drop it, finalise the previous
            // streaming bubble instead.
            const hasFinalEvent = targetMsg?.events?.some(e => e.is_final);
            const hasText = !!targetMsg?.text?.trim();
            if (targetMsg && !hasFinalEvent && !hasText) {
              return prev
                .filter(msg => msg.id !== finalId)
                .map(msg => (msg.sender === 'bot' && msg.isStreaming ? finalizeMessage(msg) : msg));
            }
            // Drop ORDER_CREATED-only bubbles (silent side-effects).
            if (targetMsg && isOrderCreatedOnly(targetMsg.text)) {
              return prev.filter(msg => msg.id !== finalId);
            }
            const finalized = prev.map(msg => (msg.id !== finalId ? msg : finalizeMessage(msg)));
            // Dedup consecutive bot bubbles with identical text — happens when a
            // continuation opens a new bubble whose content matches the bubble
            // just finalized (e.g. the VIN-decoded greeting being re-emitted).
            const deduped: typeof finalized = [];
            for (const msg of finalized) {
              const prevMsg = deduped[deduped.length - 1];
              const sameText =
                prevMsg &&
                prevMsg.sender === 'bot' &&
                msg.sender === 'bot' &&
                !!prevMsg.text?.trim() &&
                prevMsg.text === msg.text;
              if (sameText) continue;
              deduped.push(msg);
            }
            return deduped;
          });
          if (isSessionActive(sessionId)) {
            setIsLoading(false);
            setRetryState(null);
          }
        },

        // onError
        (error: string, errorDetails?: any) => {
          clearStreaming();
          const errorId = activeBotMessageIdRef.current;
          logAnalyticsEvent?.('chat_error', { error, timestamp: new Date().toISOString() });
          if (userMessageId) {
            setMessages(prev => markMessagesAsError(prev, userMessageId, errorId, error, errorDetails));
          } else {
            // No user message to mark (hidden trigger) — just mark bot message as error
            setMessages(prev =>
              prev.map(msg =>
                msg.id === errorId
                  ? { ...msg, isStreaming: false, isError: true, errorDetails: errorDetails ?? { message: error } }
                  : msg
              )
            );
          }
          if (isSessionActive(sessionId)) {
            setIsLoading(false);
            setRetryState(null);
            // Schedule a background history reload — the server may have finished the
            // request even though the client-side stream failed (network blip, timeout,
            // etc.).  This gives the user the actual response without needing to retry.
            onStreamError?.(sessionId);
          }
        },

        contextTags,
        filesData,
        controller.signal,
        onRetryWithReset,
        structuredAnswer,
        partSelectionMode,
      );
    } catch (error) {
      clearStreaming();
      const catchId = activeBotMessageIdRef.current;

      if (controller.signal.aborted) {
        // User deliberately cancelled — remove the active bot placeholder
        setMessages(prev => removeBotMessage(prev, catchId));
        if (isSessionActive(sessionId)) {
          setIsLoading(false);
        }
        setAbortController(null);
        activeAbortControllerRef.current = null;
        return;
      }

      logAnalyticsEvent?.('chat_error', { error: String(error), timestamp: new Date().toISOString() });

      const errorDetails =
        error instanceof Error
          ? { message: error.message, stack: error.stack, name: error.name, cause: error.cause }
          : { message: String(error) };

      if (userMessageId) {
        setMessages(prev => markMessagesAsError(prev, userMessageId, catchId, String(error), errorDetails));
      } else {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === catchId
              ? { ...msg, isStreaming: false, isError: true, errorDetails }
              : msg
          )
        );
      }
      if (isSessionActive(sessionId)) {
        setIsLoading(false);
        onStreamError?.(sessionId);
      }
      setAbortController(null);
      activeAbortControllerRef.current = null;
    }
  }, [
    setMessages,
    setIsLoading,
    setRetryState,
    activeAbortControllerRef,
    setAbortController,
    isSessionActive,
    onRetry,
    onStreamError,
    logAnalyticsEvent,
    partSelectionMode,
  ]);

  return { send, abort, isStreamingRef };
}
